/**
 * Language detection.
 *
 * Decision tree (in order):
 *   1. Manual override (localStorage 'app:lang') — user explicitly picked.
 *   2. Geolocation by IP — BR/PT → pt, hispanic country → es.
 *      (VPN naturally affects this since IP is what is being checked.)
 *   3. Browser/OS language preferences — pt-* → pt, es-* → es.
 *   4. Fallback → en.
 *
 * Geolocation is fetched from ipapi.co with a short timeout and cached in
 * sessionStorage for the rest of the tab session so we don't burn the
 * free quota on every navigation.
 */
import type { Lang } from '.'

const LANG_STORAGE_KEY = 'app:lang'
const GEO_CACHE_KEY = 'app:geo-country'
const GEO_TIMEOUT_MS = 1500

const SPANISH_COUNTRIES = new Set([
  'ES', 'MX', 'AR', 'CO', 'CL', 'PE', 'VE', 'UY', 'EC',
  'BO', 'PY', 'CR', 'PA', 'SV', 'HN', 'NI', 'DO', 'CU',
  'PR', 'GT',
])

const PORTUGUESE_COUNTRIES = new Set(['BR', 'PT'])

export function getStoredLang(): Lang | null {
  try {
    const value = localStorage.getItem(LANG_STORAGE_KEY)
    if (value === 'pt' || value === 'en' || value === 'es') {
      return value
    }
  } catch {
    // localStorage unavailable (private mode, SSR, etc.) — ignore.
  }
  return null
}

export function storeLang(lang: Lang): void {
  try {
    localStorage.setItem(LANG_STORAGE_KEY, lang)
  } catch {
    // Ignore failures — manual choice just won't persist across sessions.
  }
}

function detectFromBrowser(): Lang {
  const candidates = (navigator.languages?.length ? navigator.languages : [navigator.language]) || []
  for (const raw of candidates) {
    const tag = (raw || '').toLowerCase()
    if (tag.startsWith('pt')) return 'pt'
    if (tag.startsWith('es')) return 'es'
  }
  return 'en'
}

function langFromCountry(countryCode: string | null | undefined): Lang | null {
  if (!countryCode) return null
  const cc = countryCode.toUpperCase()
  if (PORTUGUESE_COUNTRIES.has(cc)) return 'pt'
  if (SPANISH_COUNTRIES.has(cc)) return 'es'
  return null
}

function getCachedCountry(): string | null {
  try {
    return sessionStorage.getItem(GEO_CACHE_KEY)
  } catch {
    return null
  }
}

function setCachedCountry(country: string): void {
  try {
    sessionStorage.setItem(GEO_CACHE_KEY, country)
  } catch {
    // Ignore.
  }
}

async function fetchCountryCode(): Promise<string | null> {
  const cached = getCachedCountry()
  if (cached) return cached

  const controller = new AbortController()
  const timer = setTimeout(() => controller.abort(), GEO_TIMEOUT_MS)

  try {
    const response = await fetch('https://ipapi.co/json/', {
      signal: controller.signal,
      headers: { Accept: 'application/json' },
    })
    if (!response.ok) return null
    const data = await response.json()
    const country = typeof data?.country_code === 'string' ? data.country_code : null
    if (country) setCachedCountry(country)
    return country
  } catch {
    return null
  } finally {
    clearTimeout(timer)
  }
}

/**
 * Sync initial guess used to seed the UI before the geolocation lookup
 * finishes. Returns the stored manual choice if any, otherwise the browser
 * language. The provider refines this asynchronously via `resolveLang`.
 */
export function detectInitialLang(): Lang {
  const stored = getStoredLang()
  if (stored) return stored
  return detectFromBrowser()
}

export async function detectGeoLang(): Promise<Lang | null> {
  const country = await fetchCountryCode()
  return langFromCountry(country)
}

/**
 * Full detection used by the provider on first mount.
 *
 * Returns the language to apply. Geolocation has priority over the
 * browser language (per product decision). The function never throws —
 * any failure falls back to the browser/OS preference, then English.
 */
export async function resolveLang(): Promise<Lang> {
  const stored = getStoredLang()
  if (stored) return stored

  const geo = await detectGeoLang()
  if (geo) return geo

  return detectFromBrowser()
}

export { LANG_STORAGE_KEY }
